import { cloneDeep, sortBy } from 'lodash';
import type { IService } from '@common/interfaces';
import type { IAddCarePathSettings } from '../../../../../common/interfaces/addCarePathSettings';
import type { IEpisodeCategoryBasic } from '../../../../../common/interfaces/episodeCategory';
import type { IServiceOrganizationSettings } from '../../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { IOrganization } from '../../../interfaces/organization';
import type {
    IOrganizationAddCarePathState,
    IRole,
    IServiceGroupWithProfiles,
} from '../../../interfaces/organizationAddCarePathState';
import type { AddCarePathSteps } from './constants/addCarePathSteps';

export type AddCarePathAction =
    | { organization: IOrganization; type: 'setOrganization' }
    | { currentStep: AddCarePathSteps; type: 'setCurrentStep' }
    | { episodeCategory: IEpisodeCategoryBasic | undefined; type: 'setEpisodeCategory' }
    | { serviceGroups?: IServiceGroupWithProfiles[]; type: 'setServiceGroups' }
    | { serviceGroupId: string; services: IService[]; shouldResetServiceGroup?: boolean; type: 'setServices' }
    | { roles: IRole[]; type: 'setRoles' }
    | { serviceGroups: IServiceGroupWithProfiles[]; type: 'setSettings' }
    | {
          serviceId: string;
          serviceOrganizationSettings: IServiceOrganizationSettings;
          type: 'setServiceOrganizationSettings';
      }
    | { serviceId: string; type: 'removeServiceOrganizationSettings' }
    | { defaultProfileSettings: { [key: string]: IAddCarePathSettings }; type: 'setDefaultProfileSettings' };

export function organizationAddCarePathReducer(
    state: IOrganizationAddCarePathState,
    action: AddCarePathAction
): IOrganizationAddCarePathState {
    switch (action.type) {
        case 'setOrganization': {
            return {
                ...state,
                organization: action.organization,
            };
        }
        case 'setCurrentStep': {
            return {
                ...state,
                currentStep: action.currentStep,
            };
        }
        case 'setEpisodeCategory': {
            return {
                ...state,
                episodeCategory: action.episodeCategory,
                serviceGroups: undefined,
                roles: undefined,
                serviceOrganizationSettings: undefined,
            };
        }
        case 'setServiceGroups': {
            return setServiceGroups(state, action.serviceGroups);
        }
        case 'setServices': {
            return setServices(state, action.serviceGroupId, action.services, action.shouldResetServiceGroup);
        }
        case 'setRoles': {
            return {
                ...state,
                roles: action.roles,
            };
        }
        case 'setSettings': {
            return {
                ...state,
                serviceGroups: action.serviceGroups,
            };
        }
        case 'setServiceOrganizationSettings': {
            return setServiceOrganizationSettings(state, action.serviceId, action.serviceOrganizationSettings);
        }
        case 'removeServiceOrganizationSettings': {
            return removeServiceOrganizationSettings(state, action.serviceId);
        }
        case 'setDefaultProfileSettings': {
            return {
                ...state,
                defaultProfileSettings: action.defaultProfileSettings,
            };
        }
        default:
            return state;
    }
}

function setServiceGroups(state: IOrganizationAddCarePathState, serviceGroups?: IServiceGroupWithProfiles[]) {
    const roles = state.roles?.filter((role) =>
        serviceGroups?.some((servicegroup) => servicegroup.id === role.serviceGroupId)
    );

    let services: IService[] = [];
    for (const serviceGroup of serviceGroups ?? []) {
        services.push(...(serviceGroup.services ?? []));
    }
    services = sortBy(services, (s) => s.name);

    const serviceOrganizationSettings = state.serviceOrganizationSettings?.filter((settings) =>
        services.some((service) => service.id === settings.serviceId)
    );

    return {
        ...state,
        serviceGroups,
        roles,
        serviceOrganizationSettings,
    };
}

function setServices(
    state: IOrganizationAddCarePathState,
    serviceGroupId: string,
    services: IService[],
    shouldResetServiceGroup?: boolean
) {
    const newState = cloneDeep(state);

    const serviceGroup = newState.serviceGroups?.find((serviceGroupItem) => serviceGroupItem.id === serviceGroupId);
    if (serviceGroup) {
        serviceGroup.services = services;

        if (shouldResetServiceGroup) {
            serviceGroup.profiles = undefined;
            newState.roles = newState.roles?.filter((role) => role.serviceGroupId !== serviceGroupId);
        }
        if (newState.serviceOrganizationSettings) {
            for (let i = newState.serviceOrganizationSettings.length - 1; i >= 0; i--) {
                const serviceOrganizationSettings = newState.serviceOrganizationSettings[i];

                if (!services.some((service) => service.code === serviceOrganizationSettings.service.code)) {
                    newState.serviceOrganizationSettings.splice(i, 1);
                }
            }
        }
    }

    return newState;
}

function setServiceOrganizationSettings(
    state: IOrganizationAddCarePathState,
    serviceId: string,
    updatedServiceOrganizationSettings: IServiceOrganizationSettings
) {
    let serviceOrganizationSettings = state.serviceOrganizationSettings ?? [];
    const index = serviceOrganizationSettings.findIndex(
        (organizationSettings) => organizationSettings.serviceId === serviceId
    );
    if (index === -1) {
        serviceOrganizationSettings.push(updatedServiceOrganizationSettings);
        serviceOrganizationSettings = serviceOrganizationSettings.sort((a, b) =>
            a.service.name.localeCompare(b.service.name)
        );
    } else {
        serviceOrganizationSettings[index] = updatedServiceOrganizationSettings;
    }

    return {
        ...state,
        serviceOrganizationSettings,
    };
}

function removeServiceOrganizationSettings(state: IOrganizationAddCarePathState, serviceId: string) {
    const serviceOrganizationSettings = (state.serviceOrganizationSettings ?? []).filter(
        (setting) => setting.serviceId !== serviceId
    );

    return {
        ...state,
        serviceOrganizationSettings,
    };
}
